import React from "react";
import { Lead, Section, P, Bullets, Callout, Img, CTA } from "../../../../blog/components";
import shotThreePrompts from "../../../../assets/blog/dashboard-three-prompts.jpg";
import shotDefault from "../../../../assets/blog/dashboard-default-llm.jpg";

export const title = "Por qué los LLM no saben diseñar dashboards";
export const excerpt =
  "Pídele un dashboard a cualquier modelo y recibirás siempre el mismo: cuatro tarjetas, un gráfico de líneas, un donut y una tabla. El código es excelente. El diseño está vacío. Una opinión sobre por qué esto se repite.";
export const heroAlt = "Llanura de estilo Moebius que se extiende plana hasta el horizonte";
export const readingTime = "8 min de lectura";

export default function Post() {
  return (
    <>
      <Lead>
        Los modelos escriben un código de dashboard precioso. Lo que no hacen es diseñar dashboards.
        Son dos habilidades distintas, y solo una está en los datos de entrenamiento.
      </Lead>

      <P>
        Pruébalo. Pide un dashboard de analítica, un panel de administración SaaS, una vista para el
        equipo de pagos. Productos distintos, usuarios distintos, decisiones distintas. Recibirás la
        misma página tres veces, con el color de acento rotado.
      </P>

      <Img
        src={shotThreePrompts}
        alt="Tres dashboards generados desde tres prompts distintos, idénticos en su estructura"
        caption="Tres prompts, una sola estructura. Reconstrucciones del resultado por defecto, dibujadas para este artículo."
      />

      <P>
        Barra lateral con seis o siete elementos. Cuatro tarjetas de métricas arriba. Un gráfico de
        área ancho con relleno degradado. Un donut al lado. Una tabla de actividad reciente abajo.
        Todas las tarjetas del mismo tamaño, todos los números del mismo peso visual, todas las
        variaciones en verde y hacia arriba.
      </P>

      <Section title="El dashboard por defecto">
        <P>
          Esta es la versión completa, generada de una sola pasada y sin más indicaciones. No hay nada
          roto en ella. El espaciado es consistente, la escala tipográfica es razonable, el contraste
          pasa, los componentes son reutilizables. Como pieza de ingeniería frontend es mejor que
          buena parte de lo que se publica a mano.
        </P>

        <Img
          src={shotDefault}
          alt="Un dashboard genérico generado por IA: degradado morado, cuatro tarjetas de métricas idénticas, gráfico de área, donut y tabla de ventas recientes"
          caption="La forma que recibes por defecto. Cabe en una pantalla y no te dice nada."
        />

        <P>
          Ahora intenta usarlo. ¿El negocio va bien esta mañana? ¿Alguien debería hacer algo? ¿Por
          cuál de esos cuatro números despertarías a una persona? La interfaz no tiene opinión. Te da
          ingresos, suscripciones, ventas y usuarios activos exactamente con el mismo peso visual,
          porque no sabe cuál importa y no tiene forma de averiguarlo.
        </P>
        <P>
          Si esos números te resultan familiares, es por algo. “45.231,89 $, un 20,1% más que el mes
          pasado” y “Olivia Martin, olivia.martin@email.com” salen de la demo de una librería de
          componentes muy copiada. El modelo no está inventando un dashboard. Lo está recitando.
        </P>
      </Section>

      <Section title="El código no es el problema">
        <P>
          Es tentador archivar esto como “la IA es mala con el CSS”. No lo es. El fallo ocurre un
          nivel por encima del código, en la parte del diseño que sucede antes de renderizar nada:
        </P>
        <Bullets
          items={[
            "Decidir para qué sirve esta pantalla, y para qué no.",
            "Decidir qué único número merece ser lo más grande de la página.",
            "Decidir qué se elimina para que lo importante tenga sitio.",
            "Decidir qué debe hacer quien la lee cuando el número es malo.",
          ]}
        />
        <P>
          No son decisiones de estilo. Son afirmaciones sobre el negocio, y un dashboard no es otra
          cosa que la suma de esas afirmaciones expresada en tamaños de fuente. Todo dashboard es un
          argumento sobre qué importa. Un modelo que no se juega nada en el resultado no tiene ningún
          argumento que defender, así que produce la única distribución que no exige tomar postura:
          todo, por igual.
        </P>
        <Callout>
          El peso visual idéntico es la hipótesis nula del layout. Es lo que sale cuando todavía no se
          ha decidido nada.
        </Callout>
      </Section>

      <Section title="Por qué podría ser así">
        <P>
          Tengo cuatro teorías. Ninguna es “los modelos aún no son lo bastante listos”, que me parece
          la explicación menos interesante de todas.
        </P>
      </Section>

      <Section title="1. Aprendió la imagen, no el trabajo">
        <P>
          Los dashboards operativos de verdad viven detrás de un login. Los que mira un responsable de
          soporte durante un incidente, los que un jefe de almacén tiene en una pantalla de pared, los
          que una mesa de trading lee de verdad. Nada de eso está en la web pública, y por tanto nada
          de eso está en los datos de entrenamiento.
        </P>
        <P>
          Lo que sí es público: capturas de portfolio, marketplaces de plantillas, demos de librerías
          de componentes, repos de tutoriales, galerías de inspiración. Todos esos artefactos están
          optimizados para verse bien en miniatura ante alguien que nunca los usará. Es un objetivo
          completamente distinto de ser legible a las tres de la mañana para quien ya conoce el
          sistema.
        </P>
        <P>
          Así que el modelo aprendió las convenciones del género con una fidelidad casi perfecta. Solo
          que las aprendió del material de marketing. Le pides un dashboard y te da una fotografía de
          un dashboard, que es exactamente lo que le enseñaron.
        </P>
      </Section>

      <Section title="2. Diseñar es restar, y restar no tiene recompensa">
        <P>
          Las buenas pantallas operativas son sobre todo el resultado de eliminaciones. Quitas la
          métrica sobre la que nadie actuó nunca, el gráfico que estaba ahí solo porque existía el
          dato, el tercer filtro, el número de vanidad que pidió el fundador en 2023. Lo que sobrevive
          es pequeño, denso, algo feo, y funciona.
        </P>
        <P>
          Ahora piensa cómo se ve eliminar desde la posición del modelo. Añadir una tarjeta es gratis
          y parece servicial. Quitarla es apostar a que el usuario no la quería, y si la apuesta falla,
          la respuesta parece perezosa o incompleta. Un entrenamiento que premia ser útil premia la
          cobertura. Y cobertura significa más tarjetas.
        </P>
        <P>
          El resultado es un sistema estructuralmente aditivo al que le pedimos un oficio
          estructuralmente sustractivo. Se cubre las espaldas, y cubrirse las espaldas en layout tiene
          exactamente el aspecto de una rejilla de tarjetas del mismo tamaño.
        </P>
      </Section>

      <Section title="3. No se juega nada, así que no puede jerarquizar">
        <P>
          La jerarquía exige consecuencias. Pones un número enorme porque llegar tarde a ese número
          cuesta dinero o despierta a alguien. Ese conocimiento no está en el prompt y normalmente
          tampoco en el código. Está en la cabeza de quien opera el sistema, en lo que se rompió el
          trimestre pasado, en qué pregunta hace qué directivo los lunes.
        </P>
        <P>
          A un modelo al que le pides diseñar un dashboard le estás pidiendo ordenar por importancia
          cosas cuyas consecuencias no puede observar. Negarse a ordenar es, sinceramente, el
          comportamiento correcto ante esa incertidumbre. El problema es que leemos esa negativa como
          un diseño, porque llega con aspecto de diseño.
        </P>
      </Section>

      <Section title="4. Parecerse es el objetivo equivocado">
        <P>
          La objeción evidente: hoy los modelos ven imágenes. Devuélvele el resultado a un modelo
          multimodal y te hará una crítica encantado.
        </P>
        <P>
          La hará, y la crítica irá sobre el parecido. ¿Esto parece un dashboard profesional? Sí. ¿El
          espaciado es consistente? Sí. Adelante. Lo que no puede hacer es lo que hace un diseñador
          cuando mira su propia pantalla y siente una pequeña incomodidad física, porque sabe que la
          persona que la usará tiene noventa segundos y la respuesta no está ahí. Esa incomodidad
          viene de haber visto a alguien usar la cosa. No es una propiedad visual, así que mirar más
          no la recupera.
        </P>
      </Section>

      <Section title="La captura que te debo, y que no puedo darte">
        <P>
          Este es el punto del artículo donde va la imagen del “después”. Los mismos datos, bien
          hechos, para que veas la diferencia. Construí una. No está aquí, y el motivo por el que no
          está aquí es el argumento.
        </P>
        <P>
          La renderizó la misma máquina que produjo la primera captura, esta vez a partir de mi
          jerarquía y no de la suya: un recuento de fallos enorme, el pico marcado en el despliegue que
          lo causó, la tabla ordenada por ingresos perdidos, un rollback sugerido donde antes estaba el
          donut. Parecía más seria que la versión por defecto. Puesta al lado de una pantalla a la que
          de verdad te llaman de madrugada, seguía siendo una fotografía de un dashboard, y publicarla
          aquí habría sido justo el movimiento del que se queja este artículo: una interfaz optimizada
          para que la mire alguien que nunca la va a usar.
        </P>
        <P>
          Porque lo que hace buena a una pantalla operativa no se ve en una captura. Es un residuo del
          uso. Es la columna que alguien exigió recuperar la semana después de una caída, la métrica
          que se eliminó cuando quedó claro que nadie había actuado nunca según ella, el umbral que se
          movió tres veces hasta que dejó de despertar a la gente a las 4am, el orden de las filas que
          solo tiene sentido si sabes qué proveedor miente en su página de estado. Nada de eso es una
          propiedad visual. No se puede renderizar, y no puedo falsificarlo lo bastante bien como para
          que te merezca la pena.
        </P>
        <P>
          Y por eso mismo los dashboards que mejor fotografían son los que todavía no ha usado nadie,
          que es el problema de los datos de entrenamiento del principio llamando a mi propia puerta.
          Así que quédate con la descripción en lugar de la imagen: la pantalla es para quien está de
          guardia, dice de un vistazo si el checkout está roto, y dice qué hacer al respecto. Si es
          buena no se decide por cómo se ve aquí. Se decide a las 4am, y lo decide alguien que no
          eligió estar despierto.
        </P>
      </Section>

      <Section title="Lo que queda es un renderizador, no un diseñador">
        <P>
          Fíjate, eso sí, en lo que sí ocurrió en ese intento. Un modelo renderizó la pantalla. Un
          modelo no la diseñó. Ninguna de esas decisiones salió de él: ni qué número tiene derecho a ser
          gigante, ni que el pico necesitaba el despliegue marcado encima, ni que el donut tenía que
          desaparecer, ni que un rollback vale más que cuatro métricas más. Todas se le entregaron, y
          hubo que entregárselas en varias rondas. Si se le deja decidir en cualquier paso, vuelve a lo
          de siempre: propone otra tarjeta, otro gráfico porque el dato existe, un delta verde sobre un
          número sobre el que nadie actúa.
        </P>
        <P>
          Así que esto no es una queja sobre la tecnología. Es una afirmación sobre dónde se ha movido
          el valor. Renderizar está resuelto, y eso vale mucho de verdad. Lo que no se ha movido es el
          criterio sobre qué importa, y ninguna mejora del modelo lo moverá, porque ese insumo
          sencillamente no está dentro de la máquina.
        </P>
        <P>
          La forma práctica de ese criterio es pequeña y aburrida, y por eso es tan fácil saltársela:
        </P>
        <Bullets
          items={[
            "Di quién lee esta pantalla y en qué momento.",
            "Di qué número decide por sí solo si esa persona actúa.",
            "Di qué hace a continuación, y ponlo también en la pantalla.",
            "Di explícitamente qué hay que eliminar, porque por su cuenta no eliminará nada.",
          ]}
        />
        <P>
          Dale eso a un modelo y construirá la cosa rápido, en el framework que prefieras, y te
          ahorrará un día entero de trabajo real. No le des nada y producirá la media de todas las
          capturas de dashboard de internet: algo que ha aprendido a hacer de verdad, y algo inútil de
          mirar. Lo que nunca hará es avisarte de que se te olvidó decidir.
        </P>
        <P>
          La parte incómoda es que esto vale para casi todo el trabajo con agentes, no solo para
          dashboards. El cuello de botella nunca fue teclear.
        </P>
      </Section>

      <Callout>
        Todo dashboard es alguien diciendo “esto es lo que importa”. Si nadie lo dijo, la pantalla no
        dirá nada, por bien construida que esté.
      </Callout>

      <CTA label="Conoce CATE" href="/" />
    </>
  );
}
